import React, { useContext, useEffect, useState } from 'react';
import styles from './PlayerSessions.module.scss';
import { Table, Pagination, EmptyState, BoxSuccess } from 'esa-ui-library';
import { playerPages, SESSIONS_SEARCH_TYPES, useStore } from '../../../common';

import {
	Header,
	PageAuth,
	PageContainer,
	Sidebar,
	SideMenu,
	Page,
	PageHeader,
	GlobalAlert,
	Filters,
} from '../../components';
import { PERMISSIONS } from '../../constants';
import { AuthContext } from '../../../logic';
import { getRows } from './RowProvider';
import {
	SearchFilter,
	PartnersFilter,
	DateFilter,
	DevicesFilter,
	GamesFilter,
	ProvidersFilter,
	SessionStatusFilter,
} from '../../components/Filters/components';
import { TableActions } from '../../components/TableActions/TableActions';
import { tableActions } from '../../components/TableActions/TableActions.const';
import { sessionsListMap } from './PlayerSessions.const';
import { getSessionsExport } from '../../../api';
import { filterSessionFieldsByEnv, getFiltersUrl } from './PlayerSessions.helper';
import { TransactionDetails } from './components/TransactionDetails/TransactionsDetails';
import { PropertyType } from 'esa-ui-library/lib/types';
import { find, isEmpty } from 'lodash';
import { getColumns, getInitialColumns } from '../../../common/services/getColumns';
import { useLocation } from 'react-router-dom';
import { filterSearchOptionsByEnv } from './PlayerSessions.helper';

function PlayerSessions(): JSX.Element {
	const [state, dispatch] = useStore();
	const [selected, setSelected] = useState<PropertyType>({});
	const { sessions, rows, columns, pagesTotal, currentPage, isFilterValid, sessionsLoading, filters, perPage } = state;
	const { token, user } = useContext(AuthContext);
	const filteredSessionFields = filterSessionFieldsByEnv(sessionsListMap);

	const useQuery = () => {
		return new URLSearchParams(useLocation().search);
	};

	const query = useQuery();
	const sessionId = query.get('session_id') || '';

	const setColumnsConfig = (columns: []) => {
		return columns.map((c) => {
			const fieldPosition = find(sessionsListMap, ['label', c])?.position;
			return {
				label: c,
				cellClassName: fieldPosition ? styles[fieldPosition] : '',
			};
		});
	};

	const onAction = (action: string, session: PropertyType): void => {
		switch (action) {
			case 'transaction_details':
				setSelected(session);
				break;
			default:
				break;
		}
	};

	const onCloseDetails = (): void => {
		setSelected({});
	};

	useEffect(() => {
		dispatch('PARTNERS_INIT', user);
		dispatch('FILTERS_INIT', {
			token,
			filters: ['providers', 'games', 'devices', 'sessionStatuses'],
		});
		dispatch('INIT_COLUMNS', {
			columns: getColumns('sessionsList', filteredSessionFields),
			initialColumns: getInitialColumns(filteredSessionFields),
		});
	}, []);

	useEffect(() => {
		dispatch('PARTNERS_INIT', user);
	}, [filters]);

	useEffect(() => {
		dispatch('UPDATE_ROWS', getRows(sessions, onAction, columns));
	}, [sessions, columns]);

	const onPageChange = (page: number | string) => {
		dispatch('LOADING', true);
		dispatch('PAGE_UPDATE', { token, page, perPage });
	};

	const onPerPageChange = (value: PropertyType) => {
		const perPage = value[0].value;
		dispatch('LOADING', true);
		dispatch('PER_PAGE', perPage);
		dispatch('PAGE_UPDATE', { token, currentPage, perPage });
	};

	return (
		<>
			<Header dataId='header' />
			<PageAuth permission={PERMISSIONS.canViewOwnDetails}>
				<PageContainer>
					<Sidebar dataId='admin-sidebar'>
						<SideMenu options={playerPages} title='Player Sessions' />
					</Sidebar>
					<Page>
						<PageHeader title='Player Sessions' />
						<section className={styles.PageContent}>
							<Filters>
								<PartnersFilter />
								<ProvidersFilter />
								<GamesFilter />
								<DevicesFilter />
								<SessionStatusFilter />
								<div style={{ marginLeft: 'auto', display: 'block' }}>
									<SearchFilter searchTypes={filterSearchOptionsByEnv(SESSIONS_SEARCH_TYPES)} preselected={sessionId} />
								</div>
								<DateFilter fullWidth />
							</Filters>
							{!isFilterValid && !sessionsLoading ? (
								<div className={styles.EmptyBox}>
									<EmptyState
										title='Please enter Session ID (any), Partner Session ID or Date range to retrieve results'
										icon={<BoxSuccess />}
										dataId='empty-state-component'
									/>
								</div>
							) : (
								<>
									{(rows.length > 0 || sessionsLoading) && (
										<TableActions
											exportData={{
												api: getSessionsExport,
												csvHeaders: filteredSessionFields,
												filtersQuery: getFiltersUrl(filters),
											}}
											filters={[tableActions.EXPORT, tableActions.COLUMNS]}
										/>
									)}
									<Table
										titles={setColumnsConfig(columns)}
										data={rows}
										className={styles.SessionTable}
										rowClassName={styles.SessionRow}
										isLoading={sessionsLoading}
									/>
									{!!sessions?.length && (
										<Pagination
											showPerPage
											perPage={perPage}
											onPerPage={onPerPageChange}
											total={Math.ceil(pagesTotal / perPage)}
											current={currentPage}
											onChange={onPageChange}
										/>
									)}
								</>
							)}
						</section>
						<div>{!isEmpty(selected) && <TransactionDetails session={selected} onClose={onCloseDetails} />}</div>
					</Page>
				</PageContainer>
				<GlobalAlert />
			</PageAuth>
		</>
	);
}

export { PlayerSessions };
